import { useCallback } from "react";
import { useTranslations } from "next-intl";
import { useToast } from "@/components/Toast/Toast";
import { isServerErr } from "@/lib/api/errors/ErrorHandler";
import { translateBackendError } from "@/lib/i18n/translateBackendError";

export type FailSaveOptions = {
  /** A finished sentence to show instead of reading `cause` — e.g. the
   *  wallet's "add a guest first" block reason. */
  text?: string;
  /** Key under `errors.*` for a cause we have no copy for (default
   *  `unableToSave`) — a download is not a save. */
  fallbackKey?: string;
};

export type FailSave = (cause?: unknown, opts?: FailSaveOptions) => false;

/**
 * Shared save-failure surfacing for the configurator builders (portal /
 * calendar / wallet) and the template sidebar toggles.
 *
 * Historically each handleSave did `catch { return false }` with every
 * caller ignoring the return value — a failed PUT was completely invisible:
 * the Save button just re-armed and the admin assumed the save landed
 * (BUGS.md H6/H17, FIXED 2026-07-05 W2-E).
 *
 * Call the returned `failSave(causeOrResult)` from BOTH failure branches:
 * post-C3 handlers RETURN a ServerError value while the raw clients still
 * THROW — either way this toasts a visible error and returns `false` so the
 * existing caller chain (unsaved-changes modal, AI save-bridge) keeps its
 * boolean contract.
 *
 * The toast names the backend's code when there is copy for it
 * (`versionMismatch` → "reload", `eventPlanLocked` → "renew the plan"). It
 * used to say "a server error occurred" for every handler refusal and
 * "unknown" for every throw (M-20) — the one thing the operator needed to
 * know was thrown away here.
 */
export function useSaveErrorToast(): FailSave {
  const { showToast } = useToast();
  const tErrors = useTranslations("errors");
  return useCallback(
    (cause?: unknown, opts?: FailSaveOptions): false => {
      const fallback =
        opts?.fallbackKey && tErrors.has(opts.fallbackKey)
          ? tErrors(opts.fallbackKey)
          : tErrors("unableToSave");
      const text =
        opts?.text ??
        translateBackendError(
          tErrors,
          isServerErr(cause) || cause instanceof Error ? cause : null,
          fallback,
        );
      showToast(text, "error");
      return false;
    },
    [showToast, tErrors],
  );
}
